import React from "react";

// Company name printed on exported statements.
export const COMPANY_NAME = "Limerick Distribution Ltd.";

// Ledger endpoints send balances through PHP's number_format ("12,500.00"),
// which parseFloat alone would read as 12.
export const num = (value) => {
  const parsed = parseFloat(String(value ?? "").replace(/,/g, ""));
  return isNaN(parsed) ? 0 : parsed;
};

// Ledger reports lead with a synthetic row that carries only the opening
// balance: no date, no running balance.
export const isOpeningRow = (row) => !row.date && row.opening_balance != null;

// "D"/"C" from the ledger endpoints, "dr"/"cr" from account settings.
export const isDebit = (drCr) =>
  String(drCr || "")
    .toLowerCase()
    .startsWith("d");

// Opening, period debit/credit and closing figures for a ledger report.
export const ledgerSummary = (rows, debitOf, creditOf) => {
  const list = rows || [];
  const opening = num(list.find(isOpeningRow)?.opening_balance);
  const entries = list.filter((row) => !isOpeningRow(row));
  const debit = entries.reduce((sum, row) => sum + debitOf(row), 0);
  const credit = entries.reduce((sum, row) => sum + creditOf(row), 0);
  const last = entries[entries.length - 1];
  const closing = last ? num(last.balance) : opening;
  return { opening, debit, credit, closing, count: entries.length };
};

export const money = (value) =>
  num(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Blank rather than 0.00, for ledger columns where "no amount" and "zero"
// should not look the same.
export const moneyOrBlank = (value) =>
  value === null || value === undefined || value === "" || num(value) === 0
    ? ""
    : money(value);

export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const firstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString(
    "en-CA",
  );
};

export const lastDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toLocaleDateString(
    "en-CA",
  );
};

// Shared look for every Chart of Account screen, in the same house style the
// journal voucher, cash count and budget plan screens use.
export const ChartOfAccountStyles = () => (
  <style>
    {`
      .coa-panel {
        background: #fff;
        border: 1px solid #e6e8f0;
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
        margin-bottom: 20px;
      }
      .coa-panel-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 14px 20px;
        border-bottom: 1px solid #e6e8f0;
        font-size: 0.95rem;
        font-weight: 600;
        color: #2a2962;
      }
      .coa-head-note {
        font-size: 0.75rem;
        font-weight: 500;
        color: #8b90a5;
      }
      .coa-panel-body { padding: 20px; }
      .coa-panel label {
        display: block;
        margin-bottom: 6px;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #6b7280;
      }
      .coa-panel .form-control:focus {
        border-color: #2a2962;
        box-shadow: 0 0 0 0.2rem rgba(42, 41, 98, 0.12);
      }
      .coa-panel .required { color: #e42320; font-size: 0.5rem; vertical-align: super; }
      .coa-hint {
        display: block;
        margin-top: 5px;
        font-size: 0.72rem;
        color: #8b90a5;
        line-height: 1.5;
      }

      .coa-num {
        text-align: right;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
      }
      .coa-mono {
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        font-variant-numeric: tabular-nums;
      }

      /* Stat strip */
      .coa-stat {
        position: relative;
        background: #fff;
        border: 1px solid #e6e8f0;
        border-left: 3px solid #2a2962;
        border-radius: 8px;
        padding: 14px 16px;
        height: 100%;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
      }
      .coa-stat.is-alert { border-left-color: #e42320; }
      .coa-stat.is-ok { border-left-color: #1f9d55; }
      .coa-stat-label {
        display: block;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #8b90a5;
        margin-bottom: 4px;
      }
      .coa-stat-value {
        font-size: 1.25rem;
        font-weight: 700;
        color: #2a2962;
        line-height: 1.2;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
      }
      .coa-stat.is-alert .coa-stat-value { color: #e42320; }
      .coa-stat.is-ok .coa-stat-value { color: #1f9d55; }
      .coa-stat-sub {
        display: block;
        margin-top: 2px;
        font-size: 0.72rem;
        color: #b6bacb;
      }
      .coa-stat-icon {
        position: absolute;
        top: 14px;
        right: 16px;
        font-size: 1.1rem;
        color: #e6e8f0;
      }

      /* Filter chips */
      .coa-chip {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 5px 13px;
        border-radius: 16px;
        border: 1px solid #d7dae8;
        background: #fff;
        font-size: 0.76rem;
        font-weight: 600;
        color: #52566b;
        cursor: pointer;
        transition: border-color 0.15s ease, background 0.15s ease;
      }
      .coa-chip:hover { border-color: #b6bacb; }
      .coa-chip-count {
        display: inline-block;
        min-width: 20px;
        padding: 1px 6px;
        border-radius: 10px;
        background: #f2f3f9;
        color: #8b90a5;
        font-size: 0.7rem;
        text-align: center;
      }
      .coa-chip.is-active {
        color: #fff;
        border-color: transparent;
      }
      .coa-chip.is-active .coa-chip-count {
        background: rgba(255, 255, 255, 0.22);
        color: #fff;
      }
      .coa-chip-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        display: inline-block;
      }
      .coa-chip.is-active .coa-chip-dot { background: #fff !important; }

      /* Ledger table */
      .coa-table { width: 100%; margin-bottom: 0; }
      .coa-table thead th {
        padding: 10px 12px;
        background: #2a2962;
        color: #fff;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        border: 0;
        white-space: nowrap;
      }
      .coa-table tbody td {
        padding: 8px 12px;
        border-bottom: 1px solid #f0f1f6;
        vertical-align: middle;
        font-size: 0.86rem;
      }
      .coa-table tbody tr:hover { background: #fafbfd; }
      .coa-table tfoot th,
      .coa-table tfoot td {
        padding: 11px 12px;
        background: #f2f3f9;
        border: 0;
        border-top: 2px solid #2a2962;
        font-size: 0.92rem;
        font-weight: 700;
        color: #2a2962;
      }

      /* Pills */
      .coa-pill {
        display: inline-block;
        padding: 3px 10px;
        border-radius: 12px;
        font-size: 0.72rem;
        font-weight: 600;
      }
      .coa-pill-dr { background: #eef0f8; color: #2a2962; }
      .coa-pill-cr { background: #fdf3e2; color: #c77700; }
      .coa-pill-muted { background: #f5f6fa; color: #b6bacb; }
      .coa-pill-ok { background: #e8f6ee; color: #1f9d55; }
      .coa-pill-alert { background: #fdecec; color: #e42320; }
      .coa-code {
        display: inline-block;
        padding: 3px 9px;
        border-radius: 4px;
        border: 1px solid #e6e8f0;
        background: #f7f8fc;
        color: #2a2962;
        font-size: 0.76rem;
        font-weight: 600;
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      }

      /* Read-only key/value rows */
      .coa-kv {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 16px;
        padding: 9px 0;
        border-bottom: 1px solid #f0f1f6;
      }
      .coa-kv:last-child { border-bottom: 0; }
      .coa-kv-label {
        flex-shrink: 0;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #8b90a5;
      }
      .coa-kv-value {
        font-size: 0.88rem;
        font-weight: 600;
        color: #35374d;
        text-align: right;
        word-break: break-word;
      }

      /* Inline notices */
      .coa-notice {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 12px 16px;
        border-radius: 8px;
        border: 1px solid #e6e8f0;
        border-left: 3px solid #2a2962;
        background: #f7f8fc;
        font-size: 0.85rem;
        color: #52566b;
        margin-bottom: 20px;
      }
      .coa-notice.is-alert { border-left-color: #e42320; background: #fdf4f4; }
      .coa-notice i { color: #8b90a5; margin-top: 2px; }

      .coa-empty {
        padding: 48px 20px;
        text-align: center;
        color: #8b90a5;
      }
      .coa-empty i { font-size: 2rem; color: #d7dae8; display: block; margin-bottom: 10px; }

      @media print {
        .coa-no-print { display: none !important; }
        .coa-panel { box-shadow: none; break-inside: avoid; }
      }
    `}
  </style>
);
